import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export interface IBasicSelectItem {
    id: string;
    name: string;
    matIcon?: string | null;
}

interface ILocalBasicItem extends IBasicSelectItem {
    isSelected: boolean;
}

@Component({
    selector: 'app-sfl-selector',
    templateUrl: './sfl-selector.component.html',
    styleUrls: ['./sfl-selector.component.scss'],
})
export class SflSelectorComponent<T extends IBasicSelectItem> implements OnDestroy {
    private _allItems: T[] = [];
    private _selectedItemId: string | null = null;
    public _type: string;
    public _capitalLetterType: string;
    private searchSubject = new Subject<string>();
    private onDestroy$ = new Subject<boolean>();
    searchTerm = '';
    constructor(
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.searchSubject
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy$))
            .subscribe((term) => this.onSearch.emit(term));
    }

    @Input({ alias: 'all-items', required: true })
    set allItems(items: T[]) {
        this._allItems = items;
        this.updateLocalItems();
    }

    @Input({ alias: 'selected-item', required: true })
    set selectedItem(item: Pick<T, 'id'> | null) {
        this._selectedItemId = item?.id ?? null;
        this.updateSelectedLocalItem();
    }

    @Input({ alias: 'type', required: true })
    set selectorType(type: 'workspace' | 'organization' | string) {
        this.updateSelectorType(type);
    }

    @Input({ alias: 'minimized', required: true }) minimized: boolean = false;

    @Input({ alias: 'icon', required: true }) matIcon: string;

    @Output() onItemSelected = new EventEmitter<T>();
    @Output() onCreateNewItem = new EventEmitter<void>();
    @Output() onSettingsClick = new EventEmitter<void>();
    @Output() onSearch = new EventEmitter<string>();
    @Output() onLoadMore = new EventEmitter<void>();
    @Input({ alias: 'searchable' }) searchable: boolean = false;
    @Input({ alias: 'total-count' }) totalCount: number | null = null;
    localItems: ILocalBasicItem[] = [];
    filteredItems: ILocalBasicItem[] = [];

    selectedLocalItem: ILocalBasicItem | null = null;

    public onSelectionChanged(item: ILocalBasicItem): void {
        const originalItem = this._allItems.find((i) => i.id === item.id);
        if (originalItem) {
            this.onItemSelected.emit(originalItem);
        }
    }
    public handleLoadMore(event: MouseEvent): void {
        event.stopPropagation();
        this.onLoadMore.emit();
    }
    public handleCreateNewItem(event: MouseEvent): void {
        event.stopPropagation(); // Prevent mat-select from closing improperly
        this.onCreateNewItem.emit();
    }
    public onSearchInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.searchTerm = value;

        // If parent handles search (server-side), emit to parent
        if (this.onSearch.observed) {
            this.searchSubject.next(value);
        } else {
            // Client-side filter
            this.applyClientFilter();
        }
    }

    private applyClientFilter(): void {
        if (!this.searchTerm) {
            this.filteredItems = this.localItems;
        } else {
            const term = this.searchTerm.toLowerCase();
            this.filteredItems = this.localItems.filter((item) => item.name.toLowerCase().includes(term));
        }
    }

    public onSearchKeydown(event: KeyboardEvent): void {
        // Prevent mat-menu from intercepting keyboard events in search field
        event.stopPropagation();
    }
    public handleSettingsClick(event: MouseEvent): void {
        event.stopPropagation(); // Prevent mat-select from closing improperly

        this.onSettingsClick.emit();
    }

    private updateLocalItems(): void {
        if (!this._allItems) {
            this.localItems = [];
            this.filteredItems = [];
            return;
        }

        this.localItems = this._allItems.map((item) => ({
            ...item,
            isSelected: false,
        }));
        this.applyClientFilter();
        this.updateSelectedLocalItem();
    }

    private updateSelectedLocalItem(): void {
        if (!this._selectedItemId || !this.localItems) {
            this.selectedLocalItem = null;
            return;
        }

        const found = this.localItems.find((item) => item.id === this._selectedItemId);
        if (found) {
            this.localItems.forEach((item) => (item.isSelected = false));
            found.isSelected = true;
            this.selectedLocalItem = found;
        } else {
            this.selectedLocalItem = null;
        }
    }

    private updateSelectorType(type: string) {
        if (!type || type.length === 0) {
            return;
        }
        this._type = type;
        if (type.charAt(0) !== type.charAt(0).toUpperCase()) {
            this._capitalLetterType = type.charAt(0).toUpperCase() + type.slice(1);
        } else {
            this._capitalLetterType = type;
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }
}
